import React from "react";
import MarkdownIt from "markdown-it";
import styled from "styled-components";
import MathText, { splitMathSegments } from "./MathText";

const markdown = new MarkdownIt({ html: false, breaks: true });

export default function MarkdownMathText({ source = "", includeImages = true, formatText }) {
  const formulas = new Map();
  let prefix = "HANDOUTMATH";
  while (String(source).includes(prefix)) prefix += "X";
  const protectedSource = splitMathSegments(String(source)).map((segment) => {
    if (segment.type === "text") return segment.value;
    const marker = `${prefix}${formulas.size}END`;
    const delimiter = segment.display ? "$$" : "$";
    formulas.set(marker, `${delimiter}${segment.value}${delimiter}`);
    return marker;
  }).join("");
  const restore = (text) => {
    let result = text;
    formulas.forEach((formula, marker) => { result = result.split(marker).join(formula); });
    return result;
  };
  const renderText = (text, key) => {
    const parts = text.split(new RegExp(`(${prefix}\\d+END)`, "g"));
    return parts.map((part, index) => formulas.has(part)
      ? <MathText key={`${key}-${index}`} source={formulas.get(part)} fallback="" />
      : <React.Fragment key={`${key}-${index}`}>{formatText ? formatText(part) : part}</React.Fragment>);
  };
  const renderTokens = (tokens) => {
    let index = 0;
    const collect = () => {
      const nodes = [];
      while (index < tokens.length) {
        const token = tokens[index++];
        const key = index;
        if (token.nesting === -1) break;
        if (token.nesting === 1) {
          const children = collect();
          const props = { key };
          for (const [name, value] of token.attrs || []) {
            if (["href", "title", "start", "style"].includes(name)) {
              if (name === "style") props.style = { textAlign: value.split(":")[1] };
              else props[name] = value;
            }
          }
          nodes.push(token.hidden ? <React.Fragment key={key}>{children}</React.Fragment>
            : React.createElement(token.tag, props, children));
        } else if (token.type === "inline") {
          nodes.push(<React.Fragment key={key}>{renderTokens(token.children || [])}</React.Fragment>);
        } else if (token.type === "text") {
          nodes.push(<React.Fragment key={key}>{renderText(token.content, key)}</React.Fragment>);
        } else if (token.type === "code_inline") {
          nodes.push(<code key={key}>{restore(token.content)}</code>);
        } else if (token.type === "code_block" || token.type === "fence") {
          nodes.push(<pre key={key}><code>{restore(token.content)}</code></pre>);
        } else if (token.type === "softbreak" || token.type === "hardbreak") {
          nodes.push(<br key={key} />);
        } else if (token.type === "hr") {
          nodes.push(<hr key={key} />);
        } else if (token.type === "image") {
          if (includeImages) nodes.push(<img key={key} src={token.attrGet("src")} alt={restore(token.content)} />);
        }
      }
      return nodes;
    };
    return collect();
  };
  return <MarkdownRoot>{renderTokens(markdown.parse(protectedSource, {}))}</MarkdownRoot>;
}

const MarkdownRoot = styled.div`
  overflow-wrap: anywhere;
  p { margin: 0 0 0.6em; overflow-x: auto; }
  h1, h2, h3, h4, h5, h6 { margin: 0.6em 0 0.3em; line-height: 1.3; }
  h1 { font-size: 1.6em; }
  h2 { font-size: 1.4em; }
  h3 { font-size: 1.2em; }
  h4, h5, h6 { font-size: 1em; }
  ul, ol { margin: 0.4em 0; padding-left: 1.6em; }
  blockquote { margin: 0.5em 0; padding-left: 0.8em; border-left: 3px solid #9ca3af; }
  pre { white-space: pre-wrap; }
  table { width: 100%; border-collapse: collapse; margin: 0.5em 0; }
  th, td { border: 1px solid #9ca3af; padding: 0.3em; }
  img { max-width: 100%; }
`;
